"use client";

import { cn } from "@/lib/utils";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";
import { scrollToElement } from "@/lib/scroll";
import { useForm } from "@tanstack/react-form-nextjs";
import { useLocale, useTranslations } from "next-intl";
import { FormContentBlock } from "@/blocks/forms/content/component";
import { formBlockComponents } from "@/lib/forms/block-components";
import { buildFormDefaultValues } from "@/lib/forms/default-values";
import { getHoneypotValue } from "@/lib/forms/form-submission";
import { resolveFormFieldPublicId } from "@/lib/forms/static-field-id";
import { buildFormInputSchemaParams, performFormSubmitConfirmationRedirect } from "@/lib/forms/form-validation";
import { filterBlockWidthClass } from "@/lib/types/form/field-width";
import { Button } from "@/components/frontend/ui/button";
import { FormHoneypotField } from "@/components/frontend/ui/form/inputs/honeypot";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formFieldValuesToDataLayerData, trackDataLayerEvent } from "@/lib/data-measurements/datalayer";
import { handleFormSubmit } from "@/lib/forms/form-submit-handler";
import { FormProps, FormMetadata } from "@/lib/types/form/form";

const buttonAlignClass = {
	left: "justify-start",
	center: "justify-center",
	right: "justify-end",
};

export const Form: React.FC<FormProps> = ({ fullForm, variant, children, className, onSubmit, searchProfileConfig, metadata }) => {
	const t = useTranslations("forms");
	const locale = useLocale();
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const formStartedAtRef = useRef(Date.now());

	useEffect(() => {
		if (!isSubmitted) return;
		window.requestAnimationFrame(() => scrollToElement("#form-confirmation-message"));
	}, [isSubmitted]);

	const form = useForm({
		defaultValues: buildFormDefaultValues(fullForm.fields),
		onSubmit: async ({ value }) => {
			setSubmitError(null);

			const submitMetadata: FormMetadata = { ...metadata };

			switch (fullForm.formType) {
				case "open-subscription":
					submitMetadata.connectionId = fullForm.connectionId;
					break;
				case "search-profile":
					submitMetadata.searchProfile = value.search_profile as FormMetadata["searchProfile"];
					break;
			}

			const formSubmitResult = await handleFormSubmit(
				{
					formId: fullForm.id,
					formType: fullForm.formType,
					fieldValues: value,
					honeypot: formRef.current ? getHoneypotValue(formRef.current, "additional_info") : "",
					submittedFromPage: window.location.href,
					formStartedAt: formStartedAtRef.current,
					metadata: submitMetadata,
				},
				locale,
			);

			if (formSubmitResult === "already_subscribed") {
				setSubmitError(t("notices.alreadySubscribed"));
				return;
			}

			if (!formSubmitResult) {
				throw new Error("Failed to submit form.");
			}

			trackDataLayerEvent(`form_submit_${fullForm.title.toLowerCase().replaceAll(" ", "_")}`, {
				form_id: fullForm.id,
				form_type: fullForm.formType ?? "",
				...formFieldValuesToDataLayerData(value),
			});

			await onSubmit?.(value);

			if (performFormSubmitConfirmationRedirect(fullForm, locale)) {
				return;
			}

			form.reset();

			setIsSubmitted(true);
		},
	});

	if (isSubmitted) {
		return (
			<div id="form-confirmation-message" className={cn(`ms-form-${resolveRelationshipId(variant)}`, className)}>
				<div className="w-full text-sm border px-3 py-2 font-medium rounded-(--ms-form-el-border-radius,var(--radius-md)) border-(--ms-color-success)/40 bg-(--ms-color-success)/20 text-(--ms-color-success)">
					{children ?? t("notices.confirmationFallback")}
				</div>
			</div>
		);
	}

	return (
		<div className={cn(`ms-form-${resolveRelationshipId(variant)}`, className)}>
			<form.Subscribe selector={(state) => state.submissionAttempts > 0 && !state.isFieldsValid}>
				{(hasValidationError) => {
					if (!submitError && !hasValidationError) return null;

					return (
						<div
							id="form-error"
							role="alert"
							className="[--form-error-color:var(--ms-form-el-error-color,var(--ms-color-danger))] mb-4 inline-flex font-medium text-sm w-full items-center border px-4 py-3 rounded-(--ms-form-el-border-radius,var(--radius-md)) border-(--form-error-color)/40 bg-(--form-error-color)/20 text-(--form-error-color)"
						>
							{submitError ?? t("notices.validationError")}
						</div>
					);
				}}
			</form.Subscribe>

			<form
				ref={formRef}
				noValidate
				className="[--form-gap-x:var(--ms-form-el-spacing-horizontal,--spacing(4))] [--form-gap-y:var(--ms-form-el-spacing-vertical,--spacing(6))] flex flex-wrap gap-x-(--form-gap-x) gap-y-(--form-gap-y)"
				onSubmit={async (e) => {
					e.preventDefault();
					e.stopPropagation();
					try {
						await form.handleSubmit();
					} catch (error) {
						console.error("form.handleSubmit rejected", error);
					} finally {
						window.requestAnimationFrame(() => {
							if (document.getElementById("form-error")) {
								scrollToElement("#form-error");
							}
						});
					}
				}}
			>
				<FormHoneypotField label={t("notices.honeypotLabel")} />

				{fullForm.fields?.map((block) => {
					if (!block.id) return null;

					const containerClassName = filterBlockWidthClass("layout" in block ? block.layout?.width : undefined);

					if (block.blockType === "form-content") {
						return <FormContentBlock key={block.id} {...block} containerClassName={containerClassName} />;
					}

					if (!(block.blockType in formBlockComponents)) return null;

					const blockComponent = formBlockComponents[block.blockType];
					const Block = blockComponent.component as unknown as React.ComponentType<Record<string, unknown>>;
					const publicId = resolveFormFieldPublicId(block);

					return (
						<form.Field key={block.id} name={publicId} validators={{ onChange: blockComponent.zodSchema(buildFormInputSchemaParams(block)) }}>
							{(field) => (
								<Block
									{...block}
									variant={variant}
									containerClassName={containerClassName}
									fieldName={publicId}
									fieldValue={field.state.value}
									fieldError={field.state.meta.errors[0]?.message}
									onFieldChange={field.handleChange}
									onFieldBlur={field.handleBlur}
									searchProfileConfig={searchProfileConfig}
								/>
							)}
						</form.Field>
					);
				})}

				<div className={cn("w-full flex gap-2 items-center", buttonAlignClass[fullForm.submitButtonAlign ?? "left"])}>
					<form.Subscribe selector={(state) => state.isSubmitting}>
						{(isSubmitting) => (
							<Button type="submit" disabled={isSubmitting} variant={fullForm.submitButtonVariant}>
								<span className="relative inline-flex items-center justify-center">
									<span className={isSubmitting ? "opacity-40" : undefined}>{fullForm.submitButtonLabel}</span>
									{isSubmitting && (
										<span className="absolute inset-0 inline-flex items-center justify-center">
											<Loader2 className="size-4 animate-spin" aria-hidden="true" />
										</span>
									)}
								</span>
							</Button>
						)}
					</form.Subscribe>
				</div>
			</form>
		</div>
	);
};
